/**
 * Date filters that read the way people here write dates.
 *
 * A native `<input type="date">` renders its own empty-state hint, and Chrome
 * writes that hint in lower case — `dd-mm-yyyy`. Nothing can change it: it is
 * browser chrome rather than a placeholder, so there is no attribute, no
 * property and no stylesheet that reaches it. The only way to show
 * `DD-MM-YYYY` is to stop using a native date input and take the parsing on.
 *
 * That is a fair trade for a *filter* and a bad one for a form. A filter is
 * typed quickly by somebody who already knows the date they want, and losing
 * the calendar popover costs them nothing. A joining date or a date of birth
 * is a field somebody picks, often on a phone, and replacing that control with
 * a text box would trade a real affordance for a casing preference. So these
 * two helpers exist for filters, and the forms keep their native inputs.
 *
 * Both directions are deliberately forgiving in one direction only. Anything
 * that is not eight digits comes back as an empty string rather than a guess,
 * because a half-typed date must narrow nothing — the alternative is a list
 * that empties itself while somebody is still typing the year.
 */

/** ISO (what the API speaks) to DD-MM-YYYY (what the screen shows). */
export function formatFilterDate(isoDate: string): string {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  return year && month && day ? `${day}-${month}-${year}` : '';
}

/**
 * DD-MM-YYYY to ISO, or an empty string when it is not a real date yet.
 *
 * Digits are read positionally after every separator is stripped, so
 * `01-02-2026`, `01/02/2026` and `01022026` are the same date and somebody
 * pasting from a spreadsheet is not corrected for punctuation.
 *
 * The round-trip check at the end is what rejects the 31st of February. A
 * `new Date` will happily roll that forward to the 3rd of March, which would
 * silently filter on a date nobody typed.
 */
export function parseFilterDate(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length !== 8) return '';
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);
  const candidate = `${year}-${month}-${day}`;
  const parsed = new Date(`${candidate}T00:00:00`);
  return parsed.getFullYear() === Number(year)
    && parsed.getMonth() + 1 === Number(month)
    && parsed.getDate() === Number(day)
    ? candidate
    : '';
}

/**
 * What a filter box should accept as somebody types.
 *
 * Digits and dashes only, capped at the length of a full date. Kept beside the
 * two above because every caller needs the same rule, and a second copy is how
 * one filter starts accepting letters.
 */
export function sanitiseFilterDateInput(value: string): string {
  return value.replace(/[^\d-]/g, '').slice(0, 10);
}
